import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useCheckbookConfiguration from 'src/hooks/useCheckbookConfiguration';
import { GlCodeFormatType } from 'src/services/useGetGlMasks';

interface CheckbookConfigurationTableToolbarProps {
  selectedRows: GlCodeFormatType[];
  handleClearSelection: VoidFunction;
  handleAddFormat: VoidFunction;
  handleDelete: VoidFunction;
}

const CheckbookConfigurationTableToolbar: FC<CheckbookConfigurationTableToolbarProps> = ({
  selectedRows,
  handleClearSelection,
  handleAddFormat,
  handleDelete,
}) => {
  const { t } = useTranslation();
  const isSelectionDisabled = useMemo(() => selectedRows.length === 0, [selectedRows.length]);
  const {
    areFormatsRequired,
    checkForInvalidGLCodesAndSave,
    isCheckbookConfigPageDirty,
    glCodeFormats,
  } = useCheckbookConfiguration();

  const { deleteInfoMessage, isDeleteDisable } = useMemo(() => {
    const isDeleteDisable = !areFormatsRequired || isSelectionDisabled;
    let message: string[] = [];

    if (!areFormatsRequired) {
      message = ['Require GL Code Formats to set formats'];
    } else if (isSelectionDisabled) {
      message = ['Please select one or more record(s) to Delete'];
    }
    return {
      isDeleteDisable,
      deleteInfoMessage: message,
    };
  }, [areFormatsRequired, isSelectionDisabled]);
  const { reapplyMessage, isReapplyDisabled } = useMemo(() => {
    const areFormatsEmpty = glCodeFormats.length === 0;
    const isReapplyDisabled = isCheckbookConfigPageDirty || !areFormatsRequired || areFormatsEmpty;
    let message: string[] = [];

    if (!areFormatsRequired) {
      message = ['Require GL Code Formats to set formats'];
    } else if (isCheckbookConfigPageDirty && areFormatsEmpty) {
      message = ['Please add formats to save changes.'];
    } else if (isCheckbookConfigPageDirty) {
      message = ['Please Save Changes'];
    } else if (areFormatsEmpty) {
      message = ['Please add formats to Re-apply Formatting.'];
    } else {
      message = ['Will review all active codes and re-apply set format(s).'];
    }
    return {
      isReapplyDisabled,
      reapplyMessage: message,
    };
  }, [isCheckbookConfigPageDirty, areFormatsRequired, glCodeFormats.length]);

  return (
    <div className="w-full flex flex-wrap items-center justify-end gap-1 text-white text-sm">
      {areFormatsRequired && (
        <Button
          onClick={handleClearSelection}
          disabled={isSelectionDisabled}
          className={isSelectionDisabled ? '!bg-transparent' : ''}
          endIcon={<FontAwesomeIcon icon={faCircleXmark} />}
          color="transparent"
        >
          {t('Clear Selection')}
        </Button>
      )}
      <div className="flex-grow" />
      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        content={
          <div className="m-1 max-w-[350px]">
            <PopUpContentWrapper type={'Information'} contents={reapplyMessage} />
          </div>
        }
      >
        <Button
          color="primary"
          disabled={isReapplyDisabled}
          onClick={checkForInvalidGLCodesAndSave}
        >
          Re-apply Formatting
        </Button>
      </PopUp>

      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        content={
          <div className="m-1 max-w-[350px]">
            <PopUpContentWrapper
              type={'Information'}
              contents={['Require GL Code Formats to set formats']}
            />
          </div>
        }
        show={!areFormatsRequired}
      >
        <Button onClick={handleAddFormat} disabled={!areFormatsRequired} color="primary">
          Add New Format
        </Button>
      </PopUp>

      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        content={
          <div className="m-1 max-w-[350px]">
            <PopUpContentWrapper type={'Information'} contents={deleteInfoMessage} />
          </div>
        }
        show={isDeleteDisable}
      >
        <Button disabled={isDeleteDisable} onClick={handleDelete} color="error">
          Delete
        </Button>
      </PopUp>
    </div>
  );
};

export default CheckbookConfigurationTableToolbar;
